import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import useApi from "../../core/customhook/useApi";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import SearchableSelect from "./components/common-search&select";

const INTERVIEW_ROUNDS = [
  { key: "technical", label: "Technical Round" },
  { key: "hr", label: "HR Round" },
] as const;

type PositionType = "New Position" | "Replacement Position" | "";

interface ReplacementDetails {
  EmployeeName: string;
  EmployeeId: string;
  department: string;
  designation: string;
  replacementReason: string;
  band: string;
  ctc: string;
  location: string;
  totalExperience: number;
  qualification: string;
}

interface InterviewRounds {
  technical: number;
  hr: number;
}

interface FormData {
  positionType: PositionType;
  department: string;
  designation: string;
  level: string;
  costcenter: string;
  profile: string;
  reportsTo: string;
  location: string;
  budgetStatus: string;
  businessNeed: string;
  status: string;
  qualification: string;
  totalExperience: string;
  kras: string[];
  ctc: string;
  PositionName: string;
  manpowerApproved: string;
  overBudget: string;
  expectedJoiningDate: Dayjs | null;
  positionReportingTo: string;
  JD: string | File | null;
  NoOfEmployee?: string;
  Replacement: ReplacementDetails;
  interviewRounds: InterviewRounds;
  profileName: string;
}

const INITIAL_REPLACEMENT: ReplacementDetails = {
  EmployeeName: "",
  EmployeeId: "",
  department: "",
  designation: "",
  replacementReason: "",
  band: "",
  ctc: "",
  location: "",
  totalExperience: 0,
  qualification: "",
};

const INITIAL_FORM_DATA: FormData = {
  positionType: "",
  department: "",
  designation: "",
  profile: "",
  level: "",
  costcenter: "",
  reportsTo: "",
  qualification: "",
  totalExperience: "",
  location: "",
  status: "",
  budgetStatus: "",
  businessNeed: "",
  kras: [""],
  ctc: "",
  PositionName: "",
  manpowerApproved: "",
  overBudget: "",
  expectedJoiningDate: null,
  positionReportingTo: "",
  JD: null,
  Replacement: INITIAL_REPLACEMENT,
  interviewRounds: { technical: 0, hr: 0 },
  profileName: "",
};

// Normalizes any budget string from the API to exactly match our select option values
const normalizeBudget = (raw: string): string => {
  if (!raw) return "";
  const lower = raw
    .toLowerCase()
    .replace(/[-\s]+/g, " ")
    .trim();
  if (lower.includes("non")) return "Non Budgeted";
  if (lower.includes("over")) return "Over Budgeted";
  if (lower === "budgeted") return "Budgeted";
  return raw;
};

// Normalizes the status string from the API
const normalizeStatus = (raw: any): string => {
  if (raw == "1" || raw === "pending") return "pending";
  if (raw == "2" || raw === "approved") return "approved";
  if (raw == "3" || raw === "rejected") return "rejected";
  return raw?.toString()?.toLowerCase() || "";
};

const FieldError = ({ message }: { message?: string }) =>
  message ? <div className="field-error">{message}</div> : null;

const mapApiResponseToFormData = (response: any): FormData => ({
  positionType: response.position_type || "",
  // Raw name values — resolved to IDs in Effect 2 once dropdown data is available
  department: response.department || "",
  designation: response.designation || "",
  profile: response.profile || " ",
  level: response.band || "",
  location: response.branch_id || response.location || "",
  costcenter: response.cost_center || "",
  reportsTo: response.position_reporting_to || "",
  qualification: response.qualification || "",
  totalExperience: response.total_experience || "",
  budgetStatus: normalizeBudget(response.budget),
  businessNeed: response.business_need || "",
  kras: response.kra?.map((k: any) => k.kra) || [""],
  ctc: response.ctc || "",
  PositionName: response.position_name || "",
  manpowerApproved: response.manpower_plan || "",
  overBudget: response.justification_overBudgeted || "",
  expectedJoiningDate: response.expected_date_of_joining
    ? dayjs(response.expected_date_of_joining)
    : null,
  positionReportingTo: response.position_reporting_to || "",
  JD: response.jd || null,
  status: normalizeStatus(response.status),
  Replacement: {
    EmployeeId: response.replaced_employee || "",
    EmployeeName: response.replaced_employee || "",
    department: response.Replacement?.department || "",
    designation: response.Replacement?.designation || "",
    band: response.Replacement?.band || "",
    location: response.Replacement?.branch_name || "",
    ctc: response.replacement_ctc || "",
    totalExperience: response.replacement_total_exp || "",
    qualification: response.replacement_qualification || "",
    replacementReason: response.replacement_reason || "",
  },
  interviewRounds: {
    technical: response.technical_round || 0,
    hr: response.hr_round || 0,
  },
  profileName: response.profile_name,
});

const buildMprPayload = (formData: FormData): globalThis.FormData => {
  const payload = new globalThis.FormData();
  payload.append("position_type", formData.positionType);
  payload.append("position_name", formData.PositionName);
  payload.append("cost_center", formData.costcenter);
  payload.append("department", formData.department);
  payload.append("designation", formData.designation);
  payload.append("band", formData.level);
  payload.append("ctc", formData.ctc);
  payload.append("position_reporting_to", formData.positionReportingTo);
  payload.append("location", formData.location);
  payload.append("business_need", formData.businessNeed);
  payload.append("manpower_plan", formData.manpowerApproved);
  payload.append("total_experience", formData.totalExperience);
  payload.append("qualification", formData.qualification);
  payload.append(
    "expected_date_of_joining",
    dayjs(formData.expectedJoiningDate).format("YYYY-MM-DD"),
  );
  payload.append("budget", formData.budgetStatus);
  payload.append("justification_overBudgeted", formData.overBudget);
  payload.append("technical_round", String(formData.interviewRounds.technical));
  payload.append("hr_round", String(formData.interviewRounds.hr));
  payload.append("no_of_recuirement", "1");
  payload.append(
    "replacement_total_exp",
    String(formData.Replacement.totalExperience),
  );
  payload.append(
    "replacement_qualification",
    formData.Replacement.qualification,
  );
  payload.append("replacement_reason", formData.Replacement.replacementReason);
  payload.append("profile", formData.profile);
  payload.append("created_by", "2026-02-12");
  formData.kras.forEach((kra) => payload.append("kra[]", kra));
  if (formData.positionType === "Replacement Position") {
    payload.append("replaced_employee", formData.Replacement.EmployeeId);
    payload.append("replacement_ctc", formData?.Replacement.ctc);
  }
  if (formData.JD) {
    payload.append("jd", formData.JD);
    // console.log("JD added");
  }
  return payload;
};

// ── Status Badge Helper ─────────────────────────────────────────────────────

const StatusBadge = ({ status }: { status: string }) => {
  const config: Record<string, { label: string; className: string }> = {
    pending: { label: "Pending", className: "badge bg-warning text-dark" },
    approved: { label: "Approved", className: "badge bg-success" },
    rejected: { label: "Rejected", className: "badge bg-danger" },
  };
  const cfg = config[status] ?? {
    label: status,
    className: "badge bg-secondary",
  };
  return <span className={`${cfg.className}  px-3 py-2`}>{cfg.label}</span>;
};

// ── Main Component ──────────────────────────────────────────────────────────

const MprForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isViewMode = Boolean(id);

  // isEditMode: user clicked "Edit" on an existing MPR
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };
  // Snapshot of formData at the moment Edit is clicked — used to restore on Cancel
  const [formDataSnapshot, setFormDataSnapshot] =
    useState<FormData>(INITIAL_FORM_DATA);

  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);

  // ── API Calls ──

  const { data: mprFormData } = useApi(`/api/mpr/${id}`, {
    autoCall: isViewMode,
    method: "GET",
  });
  const { data: LeaderlistData } = useApi("/api/fetch-leaderlist", {
    method: "GET",
    autoCall: true,
  });
  const { data: LocationData } = useApi("/api/fetch-location", {
    method: "GET",
    autoCall: true,
  });

  const { data: ProfileData } = useApi("/api/fetch-profile", {
    method: "GET",
    autoCall: true,
  });

  const { data: DepartmentData } = useApi("/api/fetch-department", {
    method: "GET",
    autoCall: true,
  });

  const { data: DesignationData } = useApi("/api/fetch-designation", {
    method: "GET",
    autoCall: true,
  });

  const { data: EmployeeListData } = useApi("/api/replacement-list", {
    method: "GET",
    autoCall: true,
  });

  const { callApi: GetReplacementEmployee } = useApi("/api/replacement", {
    autoCall: false,
    method: "POST",
  });
  const { callApi: AddMprApi } = useApi("/api/add-mpr", {
    method: "POST",
    autoCall: false,
  });
  const { callApi: UpdateMprApi } = useApi(`/api/update-mpr/${id}`, {
    method: "PUT",
    autoCall: false,
  });

  // STATUS CHANGE API
  const { callApi: ApproveRejectApi } = useApi("/api/mpr-action", {
    method: "POST",
    autoCall: false,
  });

  useEffect(() => {
    if (!mprFormData?.data) return;
    const mapped = mapApiResponseToFormData(mprFormData.data);
    setFormData(mapped);
    setFormDataSnapshot(mapped);
  }, [mprFormData]);

  useEffect(() => {
    if (
      !mprFormData?.data ||
      !DepartmentData?.data ||
      !DesignationData?.data ||
      !LocationData?.data ||
      !ProfileData?.data
      // !BandData?.date
    )
      return;

    const raw = mprFormData.data;

    const deptMatch = DepartmentData.data.find(
      (d: any) => d.department === raw.department,
    );
    const desigMatch = DesignationData.data.find(
      (d: any) => d.designation === raw.designation,
    );
    const locMatch = LocationData.data.find(
      (l: any) => l.branch_name === raw.location,
    );
    // const bandMatch = BandData.date.find((b: any) => b.band === raw.band);

    setFormData((prev) => {
      const resolved = {
        ...prev,
        department: deptMatch?.ID?.toString() ?? prev.department,
        designation: desigMatch?.ID?.toString() ?? prev.designation,
        location: locMatch?.branch_id ?? prev.location,
        // level: bandMatch?.ID?.toString() ?? prev.level,
      };
      // Keep snapshot in sync so Cancel restores the resolved values too
      setFormDataSnapshot(resolved);
      return resolved;
    });
  }, [
    mprFormData,
    DepartmentData,
    DesignationData,
    LocationData /* BandData */,
  ]);

  const handleReplacementEmployeeSelect = async (emp: any) => {
    // Optimistic update so the name/id show immediately
    setFormData((prev) => ({
      ...prev,
      Replacement: {
        ...prev.Replacement,
        EmployeeId: emp?.employee_id ?? "",
        EmployeeName: emp?.employee_name ?? "",
      },
    }));

    clearError("replacementEmployee");

    if (!emp?.employee_id) return;

    try {
      const { data } = await GetReplacementEmployee({
        EmployeeId: emp.employee_id,
      });
      setFormData((prev) => ({
        ...prev,
        Replacement: {
          ...prev.Replacement,
          EmployeeId: emp.employee_id,
          EmployeeName: data?.employee_name || emp.employee_name,
          department: data?.department || "",
          designation: data?.designation || "",
          location: data?.branch_name || "",
          ctc: data?.AnnualCTC ?? "",
          totalExperience:
            data?.total_experience ?? prev.Replacement.totalExperience,
          qualification: data?.qualification ?? prev.Replacement.qualification,
        },
      }));
      clearError("replacementDepartment");
      clearError("replacementDesignation");
      clearError("replacementLocation");
      clearError("replacementCtc");
    } catch (error) {
      console.error("Error fetching replacement employee details:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to fetch employee details. Please try again.",
      });
    }
  };

  // ── Edit Mode Handlers ──

  const handleEdit = () => {
    setFormDataSnapshot(formData);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setFormData(formDataSnapshot);
    setIsEditMode(false);
  };

  // ── KRA Handlers ──

  const addKra = () => {
    setFormData((prev) =>
      prev.kras.length >= 4 ? prev : { ...prev, kras: [...prev.kras, ""] },
    );
  };

  const removeKra = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      kras: prev.kras.filter((_, i) => i !== index),
    }));
  };

  const updateKra = (index: number, value: string) => {
    setFormData((prev) => {
      const updated = [...prev.kras];
      updated[index] = value;
      return { ...prev, kras: updated };
    });
    clearError("kras");
  };

  // ── Generic Field Handler ──

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  // const updateReplacement = (
  //   field: keyof ReplacementDetails,
  //   value: string,
  // ) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     Replacement: { ...prev.Replacement, [field]: value },
  //   }));

  // };

  const updateReplacement = (
    field: keyof ReplacementDetails,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      Replacement: { ...prev.Replacement, [field]: value },
    }));
    const errorKeyMap: Partial<Record<keyof ReplacementDetails, string>> = {
      qualification: "replacementQualification",
      replacementReason: "replacementReason",
      ctc: "replacementCtc",
    };
    clearError(errorKeyMap[field] ?? `replacement${field}`);
  };

  // ── Interview Round Handler ──

  const updateRound = (type: keyof InterviewRounds, change: number) => {
    setFormData((prev) => ({
      ...prev,
      interviewRounds: {
        ...prev.interviewRounds,
        [type]: Math.max(0, prev.interviewRounds[type] + change),
      },
    }));
  };

  // ── Approve / Reject Handler ──

  const handleSubmission = async (status: "Approved" | "Rejected") => {
    const isReject = status === "Rejected";

    const result = await Swal.fire({
      title: isReject ? "Reject this MPR?" : "Approve this MPR?",
      icon: isReject ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: isReject ? "Yes, reject" : "Yes, approve",
      cancelButtonText: "Cancel",
      confirmButtonColor: isReject ? "#d33" : "#198754",
      ...(isReject && {
        input: "textarea",
        inputPlaceholder: "Enter rejection reason...",
        inputAttributes: { "aria-label": "Rejection reason" },
        preConfirm: (value: string) => {
          if (!value?.trim()) {
            Swal.showValidationMessage("Rejection reason is required");
            return false;
          }
          return value;
        },
      }),
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        mpr_id: id,
        status: status,
        ...(isReject && { rejectionReason: result.value }),
      };

      const response = await ApproveRejectApi(payload);

      if (response?.data || response?.message) {
        await Swal.fire(
          "Success!",
          isReject
            ? `MPR Rejected successfully.\nReason: ${result.value}`
            : "MPR Approved successfully.",
          "success",
        );
        // Update local status so buttons disappear immediately without refetch
        setFormData((prev) => ({
          ...prev,
          status: isReject ? "rejected" : "approved",
        }));
      }
    } catch (error) {
      console.error("Error submitting approval/rejection:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to process your request. Please try again.",
        confirmButtonText: "OK",
      });
    }
  };

  // ── Create (new form submit) ──

  const handleRequisition = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validateForm(formData, false);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      const payload = buildMprPayload(formData);
      const response = await AddMprApi(payload);
      if (response?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: `MPR Created: ${response.data.mpr_id}`,
          confirmButtonText: "OK",
        });
        navigate("/view-recruitment");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong while saving the form.",
        confirmButtonText: "OK",
      });
    }
  };

  // ── Update (edit mode submit) ──

  const validateForm = (
    data: FormData,
    editing: boolean,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!data.positionType)
      errors.positionType = "Please select a position type";
    if (!data.businessNeed?.trim())
      errors.businessNeed = "Business need is required";
    if (!data.kras.length || data.kras.some((k) => !k.trim()))
      errors.kras = "All KRA fields must be filled";

    if (data.positionType === "Replacement Position") {
      if (!data.Replacement.EmployeeId)
        errors.replacementEmployee =
          "Please select the employee being replaced";
      if (!data.Replacement.department)
        errors.replacementDepartment = "Department is required";
      if (!data.Replacement.designation)
        errors.replacementDesignation = "Designation is required";
      if (!data.Replacement.location)
        errors.replacementLocation = "Location is required";
      if (!data.Replacement.ctc) errors.replacementCtc = "CTC is required";
      if (!data.Replacement.totalExperience)
        errors.replacementTotalExperience = "Total experience is required";
      if (!data.Replacement.qualification?.trim())
        errors.replacementQualification = "Qualification is required";
      if (!data.Replacement.replacementReason?.trim())
        errors.replacementReason = "Reason for replacement is required";
    }

    if (!data.PositionName?.trim())
      errors.PositionName = "Position name is required";
    if (!data.profile) errors.profile = "Profile is required";
    if (!data.department) errors.department = "Department is required";
    if (!data.designation) errors.designation = "Designation is required";
    if (!data.positionReportingTo)
      errors.positionReportingTo = "Reporting manager is required";
    if (!data.location) errors.location = "Location is required";
    if (!data.manpowerApproved)
      errors.manpowerApproved = "This field is required";
    if (!data.totalExperience)
      errors.totalExperience = "Total experience is required";
    if (!data.qualification?.trim())
      errors.qualification = "Qualification is required";
    if (!data.expectedJoiningDate)
      errors.expectedJoiningDate = "Joining date is required";
    // JD: mandatory on create only. In edit mode, an existing JD already satisfies it.
    if (!editing && !data.JD) errors.JD = "Please attach a job description";
    if (!data.ctc) errors.ctc = "CTC is required";
    if (!data.budgetStatus) errors.budgetStatus = "Budget status is required";
    if (data.budgetStatus === "Over Budgeted" && !data.overBudget?.trim())
      errors.overBudget = "Justification is required";

    return errors;
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validateForm(formData, true);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      const payload = buildMprPayload(formData);
      const response = await UpdateMprApi(payload);
      if (response?.success) {
        await Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "MPR updated successfully.",
          confirmButtonText: "OK",
        });
        setFormDataSnapshot(formData);
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error updating form:", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong while updating the form.",
        confirmButtonText: "OK",
      });
    }
  };

  // ── Derived flags ──
  const isReadOnly = isViewMode && !isEditMode;
  // Only allow editing/approving/rejecting when status is pending
  const isPending = formData.status === "pending";

  // ── Render ──

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Manpower Requisition</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Recruitment</li>
                  <li className="breadcrumb-item active">Requisition Form</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
              {/* Edit button — only visible in view mode, only when status is pending */}
              {isViewMode && !isEditMode && isPending && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleEdit}
                >
                  <i className="ti ti-pencil me-1" />
                  Edit
                </button>
              )}
              <CollapseHeader />
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <form
                noValidate
                onSubmit={isEditMode ? handleUpdate : handleRequisition}
              >
                {/* ── MPR ID & Status Row (View Mode Only) ── */}
                {isViewMode && (
                  <section className="mb-4 pb-3 border-bottom">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">MPR ID</label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={id || ""}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label fw-semibold">Status</label>
                        <div className="mt-1">
                          <StatusBadge status={formData.status} />
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                {/* ── Position Type ── */}
                <section className="mb-4">
                  <h5 className="mb-3">Position Type</h5>
                  <div className="d-flex gap-4">
                    {(
                      ["New Position", "Replacement Position"] as PositionType[]
                    ).map((type) => (
                      <div
                        key={type}
                        className="d-flex align-items-center gap-2"
                      >
                        <input
                          type="radio"
                          name="positionType"
                          className="form-check-input"
                          checked={formData.positionType === type}
                          // onChange={() =>
                          //   !isReadOnly &&
                          //   setFormData((prev) => ({
                          //     ...prev,
                          //     positionType: type,
                          //   }))
                          // }
                          onChange={() => {
                            if (isReadOnly) return;
                            setFormData((prev) => ({
                              ...prev,
                              positionType: type,
                            }));
                            clearError("positionType");
                          }}
                          disabled={isReadOnly}
                          required
                        />
                        <label className="form-label mb-0">{type}</label>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ── Position Justification ── */}
                <section className="mb-4">
                  <h5 className="mb-3">Position Justification</h5>

                  <div className="mb-3">
                    <label className="form-label">
                      Business Need <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${errors.businessNeed ? "is-invalid" : ""}`}
                      rows={3}
                      name="businessNeed"
                      value={formData.businessNeed}
                      onChange={handleChange}
                      placeholder="Explain why this position is required"
                      disabled={isReadOnly}
                      required
                    />
                    <FieldError message={errors.businessNeed} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Top 4 Expected KRAs <span className="text-danger">*</span>
                    </label>

                    {formData.kras.map((kra, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2 gap-2"
                      >
                        <input
                          type="text"
                          className={`form-control ${
                            errors.kras && !kra.trim() ? "is-invalid" : ""
                          }`}
                          value={kra}
                          onChange={(e) => updateKra(index, e.target.value)}
                          disabled={isReadOnly}
                        />
                        {!isReadOnly && formData.kras.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => removeKra(index)}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        )}
                        {!isReadOnly &&
                          index === formData.kras.length - 1 &&
                          formData.kras.length < 4 && (
                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={addKra}
                            >
                              +
                            </button>
                          )}
                      </div>
                      // ⬆ each KRA row's d-flex div closes HERE
                    ))}
                    {/* ⬇ FieldError is a sibling of the .map() output, OUTSIDE every row's d-flex div */}
                    <FieldError message={errors.kras} />
                  </div>

                  {/* <div className="mb-3">
                    <label className="form-label">
                      Top 4 Expected KRAs <span className="text-danger">*</span>
                    </label>
                    {formData.kras.map((kra, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center mb-2 gap-2"
                      >
                        <input
                          type="text"
                          className={`form-control ${
                            errors.kras && !kra.trim() ? "is-invalid" : ""
                          }`}
                          value={kra}
                          onChange={(e) => updateKra(index, e.target.value)}
                          disabled={isReadOnly}
                          required
                        />
                        {!isReadOnly && formData.kras.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => removeKra(index)}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        )}
                        {!isReadOnly &&
                          index === formData.kras.length - 1 &&
                          formData.kras.length < 4 && (
                            <button
                              type="button"
                              className="btn btn-success"
                              onClick={addKra}
                            >
                              +
                            </button>
                          )}
                        <FieldError message={errors.kras} />
                      </div>
                    ))}
                  </div> */}
                </section>

                {/* ── Replacement Details (conditional) ── */}
                {formData.positionType === "Replacement Position" && (
                  <section className="mb-4">
                    <h5 className="mb-3">Details for Replacement Position</h5>

                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Employee Being Replaced{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <SearchableSelect
                          options={EmployeeListData?.data}
                          value={formData.Replacement.EmployeeId}
                          getId={(emp: any) => emp.employee_id}
                          getLabel={(emp: any) => emp.employee_name}
                          getSubLabel={(emp: any) => emp.employee_id}
                          onSelect={(emp: any) =>
                            handleReplacementEmployeeSelect(emp)
                          }
                          placeholder="Type employee name"
                          disabled={isReadOnly}
                        />
                        <FieldError message={errors.replacementEmployee} />
                      </div>
                    </div>

                    <div className="row mb-3 g-3">
                      {[
                        {
                          label: "Department",
                          value: formData.Replacement.department,
                        },
                        {
                          label: "Designation",
                          value: formData.Replacement.designation,
                        },
                        // { label: "Band", value: formData.Replacement.band },
                        {
                          label: "Location",
                          value: formData.Replacement.location,
                        },
                      ].map(({ label, value }) => (
                        <div className="col" key={label}>
                          <label className="form-label">
                            {label} <span className="text-danger">*</span>
                          </label>
                          <input
                            className="form-control"
                            value={value}
                            disabled
                            readOnly
                          />
                        </div>
                      ))}

                      <div className="col">
                        <label className="form-label">
                          CTC <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={formData.Replacement.ctc || ""}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(
                              /\D/g,
                              "",
                            );
                            updateReplacement("ctc", digitsOnly);
                          }}
                          onBlur={() => {
                            if (formData.Replacement.ctc) {
                              const formatted = new Intl.NumberFormat(
                                "en-IN",
                              ).format(Number(formData.Replacement.ctc));
                              updateReplacement("ctc", formatted);
                            }
                          }}
                          onFocus={() => {
                            const raw = formData.Replacement.ctc.replace(
                              /,/g,
                              "",
                            );
                            updateReplacement("ctc", raw);
                          }}
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 g-3">
                      <div className="col-md-5">
                        <label className="form-label">
                          Total Experience{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          name="totalExperience"
                          value={formData.Replacement.totalExperience}
                          onChange={(e) => {
                            const digitsOnly = e.target.value.replace(
                              /\D/g,
                              "",
                            );

                            setFormData((prev: any) => ({
                              ...prev,
                              Replacement: {
                                ...prev.Replacement,
                                totalExperience: digitsOnly,
                              },
                            }));
                          }}
                          // onChange={handleChange}
                          disabled={isReadOnly}
                          required
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">
                          Qualification <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={formData.Replacement.qualification}
                          onChange={(e) =>
                            updateReplacement("qualification", e.target.value)
                          }
                          disabled={isReadOnly}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="form-label">
                        Reason for Replacement{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className={`form-control ${errors?.replacementReason ? "is-invalid" : ""}`}
                        rows={3}
                        placeholder="Briefly explain why you are requesting a replacement"
                        value={formData.Replacement.replacementReason}
                        onChange={(e) =>
                          updateReplacement("replacementReason", e.target.value)
                        }
                        disabled={isReadOnly}
                      />
                      <FieldError message={errors.replacementReason} />
                      <small className="text-muted">
                        Please provide a brief business justification
                      </small>
                    </div>
                  </section>
                )}

                {/* ── Position Details ── */}
                <section className="mb-4">
                  <h5 className="mb-3">Details for New Position</h5>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">
                        Position Name <span className="text-danger">*</span>
                      </label>

                      <input
                        type="text"
                        className={`form-control ${errors.PositionName ? "is-invalid" : ""}`}
                        name="PositionName"
                        value={formData.PositionName}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                      />
                      <FieldError message={errors.PositionName} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Profile Name <span className="text-danger">*</span>
                      </label>
                      {/* <input
                      type="text"
                      className="form-control"
                      name="profileName"
                      value={formData.profileName}
                      onChange={handleChange}
                      disabled={isReadOnly}
                      required
                    /> */}

                      <SearchableSelect
                        options={ProfileData?.data}
                        value={formData.profile}
                        getId={(val: any) => val.ID}
                        getLabel={(val: any) => val.designation}
                        onSelect={(val: any) =>
                          handleChange({
                            target: {
                              name: "profile",
                              value: val ? val.ID : "",
                            },
                          } as any)
                        }
                        placeholder="Select profile"
                        disabled={isReadOnly}
                        required
                        name="profile"
                        className={
                          errors.replacementEmployee ? "is-invalid" : ""
                        }
                      />
                      <FieldError message={errors.department} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Department <span className="text-danger">*</span>
                      </label>
                      <div className={errors.department ? "is-invalid" : ""}>
                        <SearchableSelect
                          options={DepartmentData?.data}
                          value={formData.department}
                          getId={(val: any) => val.ID}
                          getLabel={(val: any) => val.department}
                          onSelect={(val: any) =>
                            handleChange({
                              target: {
                                name: "department",
                                value: val ? val.ID : "",
                              },
                            } as any)
                          }
                          placeholder="Select Department"
                          disabled={isReadOnly}
                          required
                          name="department"
                          className={`form-control ${errors.department ? "is-invalid" : ""}`}
                        />
                      </div>
                      <FieldError message={errors.department} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Designation <span className="text-danger">*</span>
                      </label>
                      <SearchableSelect
                        options={DesignationData?.data}
                        value={formData.designation}
                        getId={(val: any) => val.ID}
                        getLabel={(val: any) => val.designation}
                        onSelect={(val: any) =>
                          handleChange({
                            target: {
                              name: "designation",
                              value: val ? val.ID : "",
                            },
                          } as any)
                        }
                        placeholder="Select Designation"
                        disabled={isReadOnly}
                        required
                        name="designation"
                        className={`form-control ${errors.designation ? "is-invalid" : ""}`}
                      />
                      <FieldError message={errors.designation} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Position Reporting To{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <SearchableSelect
                        options={LeaderlistData?.data}
                        value={formData.positionReportingTo}
                        getId={(val: any) => val.employee_id}
                        getLabel={(val: any) =>
                          `${val.employee_name} - ${val.employee_id}`
                        }
                        onSelect={(val: any) =>
                          handleChange({
                            target: {
                              name: "positionReportingTo",
                              value: val ? val.employee_id : "",
                            },
                          } as any)
                        }
                        placeholder="Select Reporting Manager"
                        disabled={isReadOnly}
                        required
                        name="positionReportingTo"
                        className={`form-control ${errors.positionReportingTo ? "is-invalid" : ""}`}
                      />
                      <FieldError message={errors.positionReportingTo} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Location <span className="text-danger">*</span>
                      </label>
                      <SearchableSelect
                        options={LocationData?.data}
                        value={formData.location}
                        getId={(val: any) => val.branch_id}
                        getLabel={(val: any) => val.branch_name}
                        onSelect={(val: any) =>
                          handleChange({
                            target: {
                              name: "location",
                              value: val ? val.branch_id : "",
                            },
                          } as any)
                        }
                        placeholder="Select Location"
                        disabled={isReadOnly}
                        required
                        name="location"
                        className={`form-control ${errors.location ? "is-invalid" : ""}`}
                      />
                      <FieldError message={errors.location} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Manpower Plan Approved?{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.manpowerApproved ? "is-invalid" : ""}`}
                        name="manpowerApproved"
                        value={formData.manpowerApproved}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                      <FieldError message={errors.manpowerApproved} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Total Experience <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control ${errors.totalExperience ? "is-invalid" : ""}`}
                        name="totalExperience"
                        value={formData.totalExperience}
                        onChange={(e: any) => {
                          const digitsOnly = e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({
                            ...prev,
                            totalExperience: digitsOnly,
                          }));
                        }}
                        onBlur={() => {
                          const val = Number(formData.totalExperience);
                          if (formData.totalExperience && val > 50) {
                            setFormData((prev) => ({
                              ...prev,
                              totalExperience: "50",
                            }));
                          }
                        }}
                        disabled={isReadOnly}
                        required
                      />
                      <FieldError message={errors.totalExperience} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Qualification <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control ${errors.qualification ? "is-invalid" : ""}`}
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                      />
                      <FieldError message={errors.qualification} />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        Expected Date of Joining{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <DatePicker
                        className={`form-control ${errors.expectedJoiningDate ? "is-invalid" : ""}`}
                        format="DD-MMMM-YYYY"
                        value={formData.expectedJoiningDate}
                        // onChange={(date) =>
                        //   setFormData((prev) => ({
                        //     ...prev,
                        //     expectedJoiningDate: date,
                        //   }))
                        // }
                        onChange={(date) => {
                          setFormData((prev) => ({
                            ...prev,
                            expectedJoiningDate: date,
                          }));
                          clearError("expectedJoiningDate");
                        }}
                        disabledDate={(current) =>
                          current &&
                          current.startOf("day") < dayjs().startOf("day")
                        }
                        disabled={isReadOnly}
                        required
                      />
                    </div>
                    <FieldError message={errors.expectedJoiningDate} />

                    <div className="col-md-4">
                      <label className="form-label">
                        {isViewMode ? "Job Description" : "Add JD"}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      {isReadOnly ? (
                        <div>
                          <a
                            //@ts-ignore
                            href={formData.JD || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary"
                            // onClick={(e) => !formData.JD && e.preventDefault()}
                            onChange={(e) => {
                              setFormData((prev) => ({
                                ...prev,
                                //@ts-nocheck
                                //@ts-ignore
                                JD: e.target.files?.[0] || null,
                              }));
                              clearError("JD");
                            }}
                          >
                            View JD
                          </a>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            className="form-control"
                            accept=".pdf,.doc,.docx"
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                JD: e.target.files?.[0] || null,
                              }))
                            }
                          />
                          <small className="text-muted">
                            PDF and DOC/DOCX only
                          </small>
                          <FieldError message={errors.JD} />
                        </>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">
                        CTC <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control ${errors.ctc ? "is-invalid" : ""}`}
                        name="ctc"
                        type="text"
                        value={formData.ctc}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, "");
                          setFormData((prev) => ({ ...prev, ctc: digitsOnly }));
                        }}
                        onBlur={() => {
                          if (formData.ctc) {
                            const formatted = new Intl.NumberFormat(
                              "en-IN",
                            ).format(Number(formData.ctc));
                            setFormData((prev) => ({
                              ...prev,
                              ctc: formatted,
                            }));
                          }
                        }}
                        onFocus={() => {
                          const raw = formData.ctc.replace(/,/g, "");
                          setFormData((prev) => ({ ...prev, ctc: raw }));
                        }}
                        disabled={isReadOnly}
                        required
                      />
                      <FieldError message={errors.ctc} />
                    </div>

                    {/*
                    Budget option values match the API values exactly:
                    "Budgeted" | "Non Budgeted" | "Over Budgeted"
                    normalizeBudget() in mapApiResponseToFormData ensures the stored value always matches one of these.
                  */}
                    <div className="col-md-4">
                      <label className="form-label">
                        Budget Status <span className="text-danger">*</span>
                      </label>
                      <select
                        className={`form-control ${errors.budgetStatus ? "is-invalid" : ""}`}
                        name="budgetStatus"
                        value={formData.budgetStatus}
                        onChange={handleChange}
                        disabled={isReadOnly}
                        required
                      >
                        <option value="">Select</option>
                        <option value="Budgeted">Budgeted</option>
                        <option value="Non Budgeted">Non-Budgeted</option>
                        <option value="Over Budgeted">Over Budget</option>
                      </select>
                      <FieldError message={errors.budgetStatus} />
                    </div>

                    {formData.budgetStatus === "Over Budgeted" && (
                      <div className="col-md-4">
                        <label className="form-label">
                          Justification for Over Budget{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          name="overBudget"
                          value={formData.overBudget}
                          onChange={handleChange}
                          disabled={isReadOnly}
                          required
                        />
                      </div>
                    )}
                  </div>
                </section>

                {/* ── Interview Rounds ── */}
                <section className="row mb-4">
                  {INTERVIEW_ROUNDS.map(({ key, label }) => (
                    <div className="col-md-6" key={key}>
                      <label className="form-label fw-medium">
                        {label} <span className="text-danger">*</span>
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => updateRound(key, -1)}
                          disabled={isReadOnly}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="form-control text-center"
                          style={{ width: 80 }}
                          value={formData.interviewRounds[key]}
                          readOnly
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => updateRound(key, 1)}
                          disabled={isReadOnly}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </section>

                {/* ── Actions ── */}
                <div className="d-flex justify-content-end align-items-center gap-2">
                  {/* New form — Submit only */}
                  {!isViewMode && (
                    <button type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  )}

                  {/* View mode (not editing) */}
                  {isViewMode && !isEditMode && (
                    <>
                      {/* Status is pending → show Approve / Reject */}
                      {isPending && (
                        <>
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => handleSubmission("Approved")}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => handleSubmission("Rejected")}
                          >
                            Reject
                          </button>
                        </>
                      )}

                      {/* Status is approved or rejected → show read-only badge instead */}
                      {!isPending && formData.status && (
                        <StatusBadge status={formData.status} />
                      )}
                    </>
                  )}

                  {/* Edit mode — Cancel reverts changes; Update saves them */}
                  {isViewMode && isEditMode && (
                    <>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        Update
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <footer className="footer d-flex align-items-center justify-content-center border-top bg-white p-3">
          <p className="mb-0">© {new Date().getFullYear()} WeConnect. All rights reserved.</p>
        </footer>
      </div>
      <style>
        {`
        .field-error {
          color: #dc3545;
          font-size: 0.8125rem;
          margin-top: 4px;
          animation: fieldErrorIn 0.2s ease-out;
        }
        @keyframes fieldErrorIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545 !important;
          transition: border-color 0.15s ease-in-out;
        }
      `}
      </style>
    </>
  );
};

export default MprForm;
